import { useState } from 'react';
import styled from 'styled-components';
import {
  Breadcrumbs,
  Button,
  EmptyStateBox,
  Flex,
  FlexCenter,
  FlexColumn,
  Link,
  Table,
} from '@elementary/common-ui/components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus.svg';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { RUNTIMES_BASE_PATH, RUNTIMES_DOCS_URL } from './constants';
import RuntimeNameDialog from './RuntimeNameDialog';
import useRuntimes from './hooks/useRuntimes';
import useRuntimesColumns from './hooks/useRuntimesColumns';

const Wrapper = styled(FlexColumn)`
  width: 100%;
  gap: 24px;
`;

const Runtimes = () => {
  const navigate = useNavigate();
  const hasAccountPermission = useAccountPermission();
  const canEditRuntimes = hasAccountPermission('EDIT_RUNTIMES');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { runtimes, isLoading, isError, refetch, createRuntime, isCreating } =
    useRuntimes();
  const columns = useRuntimesColumns();

  return (
    <Wrapper>
      <Breadcrumbs items={['Account', 'Runtimes']} showTreeButton={false} />

      <Flex sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <FlexColumn sx={{ gap: 1 }}>
          <Typography variant="h3">Runtimes</Typography>
          <Typography variant="body1">
            Run Elementary queries inside your own network, using warehouse
            credentials that never leave your environment.{' '}
            <Link
              href={RUNTIMES_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </Link>
          </Typography>
        </FlexColumn>
        {canEditRuntimes && (
          <Button
            startIcon={<PlusIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Create runtime
          </Button>
        )}
      </Flex>

      <Table
        data={runtimes}
        columns={columns}
        isLoading={isLoading}
        urlParamsPrefix="runtimes_table"
        defaultSortKey="name"
        emptyStateRenderer={() => (
          <FlexCenter sx={{ width: '100%', py: 4 }}>
            {isError ? (
              <EmptyStateBox title="Couldn't load runtimes">
                <EmptyStateBox.ClickableText onClick={() => refetch()}>
                  Try again
                </EmptyStateBox.ClickableText>
              </EmptyStateBox>
            ) : (
              <EmptyStateBox title="No runtimes yet">
                {canEditRuntimes ? (
                  <EmptyStateBox.ClickableText
                    onClick={() => setIsCreateDialogOpen(true)}
                  >
                    Create your first runtime
                  </EmptyStateBox.ClickableText>
                ) : (
                  'Ask an account admin to create a runtime.'
                )}
              </EmptyStateBox>
            )}
          </FlexCenter>
        )}
      />

      <RuntimeNameDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        title="Create runtime"
        confirmText="Create"
        isLoading={isCreating}
        onSubmit={async (name) => {
          const runtime = await createRuntime(name);
          navigate(`${RUNTIMES_BASE_PATH}/${runtime.id}`);
        }}
      />
    </Wrapper>
  );
};

export default Runtimes;
